<template>
  <div class="d-flex align-start align-sm-center flex-column flex-sm-row flex-wrap">
    <v-switch
      v-if="isOwner"
      v-model="listIsPublic"
      class="pa-0 mr-sm-4 my-2 my-sm-0"
      :label="$t('bookList.share')"
      :disabled="privacyLoading || cartLoading"
      :loading="privacyLoading"
      :small="$vuetify.breakpoint.xs"
      hide-details
      color="success"
      @change="togglePrivacy"
    />
    <v-switch
      v-if="isOwner && publicCartSupported"
      v-model="publicCartEnabled"
      class="pa-0 mr-sm-4 my-2 my-sm-0"
      :label="$t('bookList.allowPublicCartLabel')"
      :disabled="privacyLoading || cartLoading || !listIsPublic || publicCartBlocked"
      :loading="cartLoading"
      :small="$vuetify.breakpoint.xs"
      hide-details
      color="success"
      @change="togglePublicCart"
    />
    <div
      v-if="isOwner && publicCartSupported && publicCartBlocked"
      class="text-caption tertiaryText--text mr-sm-4 my-2 my-sm-0"
    >
      {{ $t('bookList.discountedPublicCartBlocked') }}
    </div>
    <div
      v-if="isOwner || listIsPublic"
      class="d-flex align-center flex-wrap share-actions"
    >
      <share-link-chip
        :url="url"
        :disabled="!canShare"
      />
      <v-tooltip top>
        <template #activator="{ on }">
          <v-btn
            icon
            outlined
            small
            class="ml-2 share-icon-button"
            :disabled="!canShare"
            v-on="on"
            @click="openWhatsApp()"
          >
            <v-icon small>
              mdi-whatsapp
            </v-icon>
          </v-btn>
        </template>
        <span>{{ $t('common.shareWhatsApp') }}</span>
      </v-tooltip>
      <v-tooltip top>
        <template #activator="{ on }">
          <v-btn
            icon
            outlined
            small
            class="ml-2 share-icon-button"
            :disabled="!canShare"
            v-on="on"
            @click="openEmail()"
          >
            <v-icon small>
              mdi-email-outline
            </v-icon>
          </v-btn>
        </template>
        <span>{{ $t('common.shareEmail') }}</span>
      </v-tooltip>
    </div>
    <success-snackbar
      v-model="snackbar"
      :message="snackbarMessage"
    />
  </div>
</template>

<script>
  import BookListQueries from '@/graphql/BookListQueries';
  import iris from '@/iris';
  import SuccessSnackbar from '@/components/SuccessSnackbar';
  import ShareLinkChip from '@/components/ShareLinkChip.vue';

  export default {
    components: {
      SuccessSnackbar,
      ShareLinkChip,
    },
    props: {
      isOwner: {
        type: Boolean,
        required: false,
      },
      bookListId: {
        type: String,
        required: true,
      },
      isPublic: {
        type: Boolean,
        required: true,
      },
      allowPublicCart: {
        type: Boolean,
        default: false,
      },
      publicCartSupported: {
        type: Boolean,
        default: true,
      },
      discountPercentage: {
        type: [Number, String],
        default: null,
      },
      bookListName: {
        type: String,
        required: true,
      },
      bookListSlug: {
        type: String,
        default: null,
      },
    },
    data() {
      return {
        listIsPublic: this.isPublic,
        publicCartEnabled: this.isPublic && this.allowPublicCart && !(Number(this.discountPercentage) > 0),
        publicCartClearedByDiscount: Number(this.discountPercentage) > 0 && this.allowPublicCart,
        serverAllowsPublicCart: this.allowPublicCart,
        resolvedSlug: this.bookListSlug,
        privacyLoading: false,
        cartLoading: false,
        snackbar: false,
        snackbarMessage: '',
        linkCopied: false,
      };
    },
    computed: {
      canShare() {
        return this.listIsPublic && Boolean(this.resolvedSlug);
      },
      url() {
        return this.resolvedSlug ? `${window.location.origin}/q/${encodeURIComponent(this.resolvedSlug)}` : '';
      },
      publicCartBlocked() {
        return Number(this.discountPercentage) > 0;
      },
      subjectText() {
        return this.$t('bookList.shareSubject');
      },
      messageText() {
        return this.$t('bookList.shareBody', { url: this.url });
      },
    },
    watch: {
      isPublic: {
        handler(value) {
          this.listIsPublic = value;
          this.syncPublicCartEnabled();
        },
        immediate: true,
      },
      allowPublicCart: {
        handler(value) {
          this.serverAllowsPublicCart = value;
          this.publicCartClearedByDiscount = value && this.publicCartBlocked;
          this.syncPublicCartEnabled();
        },
        immediate: true,
      },
      discountPercentage: {
        handler() {
          if (this.publicCartBlocked) {
            if (this.serverAllowsPublicCart) {
              this.publicCartClearedByDiscount = true;
            }
          } else {
            this.publicCartClearedByDiscount = false;
          }
          this.syncPublicCartEnabled();
        },
        immediate: true,
      },
      bookListSlug: {
        handler(value) {
          this.resolvedSlug = value;
        },
        immediate: true,
      },
    },
    methods: {
      async mutatePublicControls(input, mutation) {
        const { data, errors } = await this.$apollo.mutate({
          mutation,
          variables: {
            input: {
              id: iris.bookList + this.bookListId,
              ...input,
            },
          },
        });

        if (errors && errors.length) {
          throw new Error(errors[0]?.message || 'Failed to update public list controls');
        }

        const updatedBookList = data?.setPrivacyBookList?.bookList;
        if (!updatedBookList) {
          throw new Error('setPrivacyBookList returned no booklist state');
        }

        return updatedBookList;
      },
      async savePublicControls(input) {
        try {
          return await this.mutatePublicControls(input, BookListQueries.setPrivacyBookList);
        } catch (error) {
          if (Object.prototype.hasOwnProperty.call(input, 'allowPublicCart') || !this.isAllowPublicCartSchemaError(error)) {
            throw error;
          }

          return this.mutatePublicControls(input, BookListQueries.setPrivacyBookListLegacy);
        }
      },
      syncPublicCartEnabled() {
        if (!this.listIsPublic || this.publicCartBlocked) {
          this.publicCartEnabled = false;
          return;
        }

        this.publicCartEnabled = this.serverAllowsPublicCart && !this.publicCartClearedByDiscount;
      },
      applyBookListState(updatedBookList) {
        if (typeof updatedBookList.isPublic === 'boolean') {
          this.listIsPublic = updatedBookList.isPublic;
        }
        if (typeof updatedBookList.allowPublicCart === 'boolean') {
          this.serverAllowsPublicCart = updatedBookList.allowPublicCart;
          this.publicCartClearedByDiscount = updatedBookList.allowPublicCart && this.publicCartBlocked;
        }
        this.syncPublicCartEnabled();
        if (updatedBookList.slug) {
          this.resolvedSlug = updatedBookList.slug;
        }
      },
      revertPublicControls() {
        this.listIsPublic = this.isPublic;
        this.serverAllowsPublicCart = this.allowPublicCart;
        this.publicCartClearedByDiscount = this.allowPublicCart && this.publicCartBlocked;
        this.syncPublicCartEnabled();
      },
      async togglePrivacy() {
        const desired = this.listIsPublic;

        this.privacyLoading = true;
        try {
          const updatedBookList = await this.savePublicControls({
            isPublic: desired,
          });
          if (typeof updatedBookList.isPublic !== 'boolean') {
            throw new Error('setPrivacyBookList returned no privacy state');
          }
          this.applyBookListState(updatedBookList);
          this.snackbarMessage = this.listIsPublic ? this.$t('bookList.sharableText') : this.$t('bookList.privateList');
          this.snackbar = true;
        } catch (error) {
          console.error('Failed to update booklist privacy:', error);
          // Revert the optimistic switch to the last server-known state.
          this.revertPublicControls();
          this.$store.dispatch('snackbar/showSnackbar', {
            text: this.$t('errors.general'),
            color: 'error',
          });
        } finally {
          this.privacyLoading = false;
        }
      },
      async togglePublicCart() {
        if (!this.publicCartSupported || !this.listIsPublic || this.publicCartBlocked) {
          this.publicCartEnabled = false;
          if (this.publicCartBlocked) {
            this.snackbarMessage = this.$t('bookList.discountedPublicCartBlocked');
            this.snackbar = true;
          }
          return;
        }

        const desired = this.publicCartEnabled;
        this.cartLoading = true;
        try {
          const updatedBookList = await this.savePublicControls({
            isPublic: true,
            allowPublicCart: desired,
          });
          if (updatedBookList.isPublic !== true || typeof updatedBookList.allowPublicCart !== 'boolean') {
            throw new Error('setPrivacyBookList returned no public cart state');
          }
          this.applyBookListState(updatedBookList);
          this.snackbarMessage = this.$t('bookList.settingsSaved');
          this.snackbar = true;
        } catch (error) {
          console.error('Failed to update public cart setting:', error);
          this.revertPublicControls();
          this.$store.dispatch('snackbar/showSnackbar', {
            text: this.$t('errors.general'),
            color: 'error',
          });
        } finally {
          this.cartLoading = false;
        }
      },
      isAllowPublicCartSchemaError(error) {
        const messages = [
          error?.message,
          ...(error?.graphQLErrors || []).map((graphQLError) => graphQLError?.message),
        ].filter(Boolean);

        return messages.some((message) => message.includes('allowPublicCart'));
      },
      openWhatsApp() {
        if (!this.canShare) return;
        window.open(`https://api.whatsapp.com/send?text=${encodeURIComponent(this.messageText)}`);
      },
      openEmail() {
        if (!this.canShare) return;
        window.open(`mailto:?subject=${encodeURIComponent(this.subjectText)}&body=${encodeURIComponent(this.messageText)}`);
      },
    },
  };
</script>
